import { Row, Col } from 'react-bootstrap';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Underline } from '@tiptap/extension-underline';
import { Markdown } from 'tiptap-markdown';
import { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import './Documentation.scss';

const MenuBar = ({ editor }) => {
  if (!editor) return null;

  return (
    <div className="tiptap-toolbar">
      <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} title="Undo">
        ↩
      </button>
      <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} title="Redo">
        ↪
      </button>
      <span className="tiptap-toolbar-divider" />
      <select
        value={
          editor.isActive('heading', { level: 1 }) ? '1' : editor.isActive('heading', { level: 2 }) ? '2' : editor.isActive('heading', { level: 3 }) ? '3' : 'p'
        }
        onChange={(e) => {
          const val = e.target.value;
          if (val === 'p') {
            editor.chain().focus().setParagraph().run();
          } else {
            editor
              .chain()
              .focus()
              .toggleHeading({ level: parseInt(val) })
              .run();
          }
        }}
      >
        <option value="p">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>
      <span className="tiptap-toolbar-divider" />
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={editor.isActive('bold') ? 'is-active' : ''} title="Bold">
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={editor.isActive('italic') ? 'is-active' : ''} title="Italic">
        <em>I</em>
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={editor.isActive('underline') ? 'is-active' : ''}
        title="Underline"
      >
        <u>U</u>
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => {
          const url = window.prompt('URL');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        className={editor.isActive('link') ? 'is-active' : ''}
        title="Link"
      >
        🔗
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        title="Bullet list"
      >
        •≡
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        title="Ordered list"
      >
        1≡
      </button>
      <span className="tiptap-toolbar-divider" />
      <button type="button" onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()} title="Insert table">
        ⊞
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={editor.isActive('codeBlock') ? 'is-active' : ''}
        title="Code block"
      >
        {'<>'}
      </button>
    </div>
  );
};

/**
 * The Documentation tab: a TipTap rich-text editor bound to the model's `documentation` (markdown). TipTap is uncontrolled —
 * it owns its own document — so this can't be a pure observer view: `onUpdate` pushes the editor's markdown into the model,
 * and the effect pushes external changes (load / reinitialize / duplicate) back into the editor, guarded against echoing.
 */
const Documentation = observer(({ entity }: { readonly entity: EditableNodeDefinition }) => {
  const documentation = entity.documentation ?? '';

  const handleUpdate = useCallback(
    ({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) => {
      entity.documentation = (editor.storage as any).markdown.getMarkdown();
    },
    [entity]
  );

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      Link.configure({ openOnClick: false }),
      Table.configure({ resizable: true }),
      TableRow,
      TableCell,
      TableHeader,
      Markdown.configure({
        html: false, // Only allow markdown, no HTML
        transformPastedText: true, // Convert pasted markdown to rich text
      }),
    ],
    content: documentation || '',
    onUpdate: handleUpdate,
  });

  useEffect(() => {
    if (!editor) return;
    // Only push in when the external value genuinely differs, so this doesn't echo against onUpdate.
    // emitUpdate:false is load-bearing: TipTap v3 defaults setContent to emitUpdate:true, which would fire onUpdate and write
    // the serializer-canonical markdown back into the model — dirtying (and on save, reformatting) any node whose stored
    // markdown isn't already canonical, just by viewing it. This programmatic sync is not a user edit.
    if ((editor.storage as any).markdown.getMarkdown() !== documentation) {
      editor.commands.setContent(documentation, { emitUpdate: false });
    }
  }, [documentation, editor]);

  return (
    <Row>
      <Col lg="12">
        <div className="tiptap-editor-wrapper">
          <MenuBar editor={editor} />
          <EditorContent editor={editor} className="tiptap-content" />
        </div>
      </Col>
    </Row>
  );
});

export default Documentation;
